import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { Pitch } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Check, 
  Shield, 
  Upload, 
  FileText, 
  AlertCircle, 
  CheckCircle, 
  XCircle, 
  Download, 
  Eye, 
  Users, 
  Briefcase, 
  ArrowRight,
  User,
  Mail,
  Phone,
  Calendar,
  Target,
  Award,
  AlertTriangle,
  FileDigit,
  Scale,
  Globe,
  Clock,
  Rocket,
  Lightbulb,
  Building2,
  Landmark,
  FileCheck,
  Hourglass,
  Link,
  Plus,
  Trash2,
  Edit
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';

interface Props {
  pitch: Pitch;
}

// Startup due diligence - only asks what pre-operation startups can provide
interface StartupDiligenceData {
  // SECTION 1: FOUNDER IDENTITY
  founders: Array<{
    id: string;
    name: string;
    role: string;
    email: string;
    phone?: string;
    idNumber?: string;
    idDocument?: string;
    experience: string;
    linkedin?: string;
    previousVentures?: string;
    timeCommitment: 'full-time' | 'part-time' | 'advisory';
    equity: number;
  }>;
  
  // SECTION 2: PROBLEM VALIDATION (from Problem Bank)
  problemId: string;
  problemStatement: string;
  solutionHypothesis: string;
  problemVerified: boolean;
  evidenceLinks?: string[]; // Links to photos/videos/data from Problem Bank
  
  // SECTION 3: ANCHOR COMMITMENT
  anchorCompany: string;
  anchorContact: string;
  anchorEmail: string;
  anchorPhone?: string;
  commitmentLevel: 'verbal' | 'loi_drafted' | 'loi_signed' | 'retainer_paid';
  loiDocument?: string;
  retainerPaid?: boolean;
  retainerAmount?: number;
  retainerDate?: string;
  
  // SECTION 4: TEAM CAPABILITY
  teamSize: number;
  keyRoles: string[];
  hiringPlan?: string;
  officeStatus: 'home' | 'coworking' | 'leased' | 'none';
  officeAddress?: string;
  
  // SECTION 5: LEGAL READINESS - Registration
  registrationStatus: 'not_started' | 'in_progress' | 'completed' | 'not_applicable';
  businessName?: string;
  businessType?: 'sole_proprietorship' | 'partnership' | 'llc' | 'company' | 'cooperative';
  registrationNumber?: string;
  registrationDate?: string;
  pinCertificate?: string;
  certificateOfIncorporation?: string;
  businessPermit?: string;
  registrationNotes?: string;
  
  // SECTION 5B: LEGAL READINESS - Bank Account
  bankStatus: 'not_started' | 'in_progress' | 'completed' | 'not_applicable';
  bankName?: string;
  bankAccount?: string;
  bankConfirmation?: string;
  
  // SECTION 5C: LEGAL READINESS - Licenses
  licenses: Array<{
    id: string;
    name: string;
    status: 'not_started' | 'in_progress' | 'completed' | 'not_applicable';
    document?: string;
    expiryDate?: string;
    notes?: string;
  }>;
  
  // SECTION 6: TIMELINE & MILESTONES
  expectedLaunchDate?: string;
  milestones: Array<{
    id: string;
    name: string;
    targetDate: string;
    status: 'pending' | 'in_progress' | 'completed';
  }>;
  
  // SECTION 7: DOCUMENTS
  documents: {
    founderIDs: string[];
    loiDocument?: string;
    registrationDocs?: string[];
    bankDocs?: string[];
    licenseDocs?: string[];
    other?: Array<{ name: string; url: string }>;
  };
  
  // Verification Status (filled by analyst)
  verified: boolean;
  verifiedAt?: string;
  verifiedBy?: string;
  verificationNotes?: string;
  riskLevel?: 'low' | 'medium' | 'high';
  readinessScore?: number;
  recommendedActions?: string[];
}

export default function DueDiligencePanel({ pitch }: Props) {
  const { user } = useAuth();
  const { submitDiligence, verifyDiligence, updatePitchStage } = useData();
  const { toast } = useToast();

  const isFounder = user?.role === 'founder' && user.id === pitch.founderId;
  const isAssignedAnalyst = user?.id === pitch.assignedAnalystId;
  const isSeniorAnalyst = user?.role === 'senior_analyst';
  
  const data = pitch.diligenceData as StartupDiligenceData | null;
  
  const [activeTab, setActiveTab] = useState('founders');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [riskLevel, setRiskLevel] = useState<'low' | 'medium' | 'high'>('medium');
  const [recommendedActions, setRecommendedActions] = useState<string[]>([]);

  // Initialize form
  const [form, setForm] = useState<Partial<StartupDiligenceData>>(() => {
    if (data) return data;
    
    return {
      founders: [{
        id: `founder-${Date.now()}-1`,
        name: '',
        role: 'Founder',
        email: user?.email || '',
        experience: '',
        timeCommitment: 'full-time',
        equity: 100
      }],
      problemId: pitch.id || '',
      problemStatement: pitch.problem || '',
      solutionHypothesis: pitch.solution || '',
      problemVerified: !!(pitch as any).analysisScores,
      evidenceLinks: [],
      anchorCompany: '',
      anchorContact: '',
      anchorEmail: '',
      commitmentLevel: 'verbal',
      teamSize: 1,
      keyRoles: ['Founder'],
      officeStatus: 'none',
      registrationStatus: 'not_started',
      bankStatus: 'not_started',
      licenses: [],
      milestones: [],
      documents: {
        founderIDs: []
      },
      verified: false
    };
  });

  // Generate unique ID
  const generateId = (prefix: string) => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  // Update field
  const updateField = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Founder management
  const updateFounder = (index: number, field: string, value: any) => {
    const updated = [...(form.founders || [])];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, founders: updated }));
  };

  const addFounder = () => {
    setForm(prev => ({
      ...prev,
      founders: [...(prev.founders || []), { 
        id: generateId('founder'),
        name: '', 
        role: '', 
        email: '', 
        experience: '', 
        timeCommitment: 'full-time', 
        equity: 0 
      }]
    }));
  };

  const removeFounder = (index: number) => {
    if ((form.founders?.length || 0) <= 1) {
      toast({
        title: "Cannot remove",
        description: "You must have at least one founder.",
        variant: "destructive"
      });
      return;
    }
    const updated = form.founders?.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, founders: updated }));
  };

  // License management
  const addLicense = () => {
    setForm(prev => ({
      ...prev,
      licenses: [...(prev.licenses || []), { 
        id: generateId('license'),
        name: '', 
        status: 'not_started' 
      }]
    }));
  };

  const updateLicense = (index: number, field: string, value: any) => {
    const updated = [...(form.licenses || [])];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, licenses: updated }));
  };

  const removeLicense = (index: number) => {
    const updated = form.licenses?.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, licenses: updated }));
  };

  // Milestone management
  const addMilestone = () => {
    setForm(prev => ({
      ...prev,
      milestones: [...(prev.milestones || []), { 
        id: generateId('milestone'),
        name: '', 
        targetDate: '', 
        status: 'pending' 
      }]
    }));
  };

  const updateMilestone = (index: number, field: string, value: any) => {
    const updated = [...(form.milestones || [])];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, milestones: updated }));
  };

  const removeMilestone = (index: number) => {
    const updated = form.milestones?.filter((_, i) => i !== index);
    setForm(prev => ({ ...prev, milestones: updated }));
  };

  // Document upload handler
  const handleDocumentUpload = (section: string, field: string, file: File | null) => {
    if (!file) return;
    
    // In production, upload to cloud storage
    const url = URL.createObjectURL(file);
    
    if (section === 'founders' && field.startsWith('founder-')) {
      // Handle founder ID documents
      const founderIndex = parseInt(field.split('-')[1]);
      const updatedFounders = [...(form.founders || [])];
      updatedFounders[founderIndex] = { 
        ...updatedFounders[founderIndex], 
        idDocument: url 
      };
      setForm(prev => ({ ...prev, founders: updatedFounders }));
    } else if (section === 'documents') {
      // Handle general documents
      setForm(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [field]: [...(prev.documents?.[field as keyof typeof prev.documents] || []), url]
        }
      }));
    } else {
      // Handle single document fields
      updateField(field, url);
    }
  };

  // Calculate readiness score
  const calculateReadiness = (data: StartupDiligenceData): number => {
    let score = 0;
    
    // Founder Identity (20%)
    let founderScore = 0;
    data.founders.forEach(founder => {
      if (founder.name) founderScore += 2;
      if (founder.email) founderScore += 2;
      if (founder.idNumber) founderScore += 2;
      if (founder.idDocument) founderScore += 2;
      if (founder.experience) founderScore += 2;
    });
    score += Math.min(20, founderScore);
    
    // Problem Validation (25%)
    if (data.problemVerified) score += 15;
    if (data.evidenceLinks?.length) score += 10;
    
    // Anchor Commitment (30%)
    if (data.commitmentLevel === 'loi_signed') score += 20;
    else if (data.commitmentLevel === 'loi_drafted') score += 10;
    else if (data.commitmentLevel === 'verbal') score += 5;
    
    if (data.retainerPaid) score += 10;
    
    // Team Capability (15%)
    if (data.teamSize > 1) score += 5;
    if (data.keyRoles.length > 1) score += 5;
    if (data.officeStatus !== 'none') score += 5;
    
    // Legal Readiness (10%)
    if (data.registrationStatus === 'completed') score += 5;
    else if (data.registrationStatus === 'in_progress') score += 2;
    
    if (data.bankStatus === 'completed') score += 3;
    else if (data.bankStatus === 'in_progress') score += 1;
    
    const completedLicenses = data.licenses?.filter(l => l.status === 'completed').length || 0;
    score += Math.min(2, completedLicenses);
    
    return Math.min(100, score);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!form.founders?.[0]?.name || !form.founders?.[0]?.email) {
      toast({
        title: "Missing founder information",
        description: "Please provide at least one founder's name and email.",
        variant: "destructive"
      });
      return;
    }

    if (!form.anchorCompany || !form.anchorContact) {
      toast({
        title: "Missing anchor information",
        description: "Please provide your committed customer's information.",
        variant: "destructive"
      });
      return;
    }

    submitDiligence(pitch.id, form as any);
    
    toast({
      title: "Due diligence submitted",
      description: "Your information has been submitted for verification.",
    });
  };

  const handleVerify = () => {
    const score = calculateReadiness(form as StartupDiligenceData);
    
    let determinedRiskLevel: 'low' | 'medium' | 'high' = 'medium';
    if (score >= 80) determinedRiskLevel = 'low';
    else if (score >= 50) determinedRiskLevel = 'medium';
    else determinedRiskLevel = 'high';
    
    // Generate recommended actions
    const actions: string[] = [];
    if (!form.founders?.every(f => f.idDocument)) actions.push('Upload ID documents for all founders');
    if (form.commitmentLevel !== 'loi_signed') actions.push('Secure signed LOI from anchor');
    if (form.registrationStatus !== 'completed') actions.push('Complete business registration');
    if (form.bankStatus !== 'completed') actions.push('Set up business bank account');
    
    const verifiedData = {
      ...form,
      verified: true,
      verifiedAt: new Date().toISOString(),
      verifiedBy: user?.id,
      verificationNotes,
      riskLevel: determinedRiskLevel,
      readinessScore: score,
      recommendedActions: actions
    };
    
    verifyDiligence(pitch.id);
    updatePitchStage(pitch.id, 'funding');
    
    toast({
      title: "Due diligence verified",
      description: `Score: ${score}/100 - Risk Level: ${determinedRiskLevel}. Moving to funding stage.`,
    });
  };

  // If already verified
  if (data?.verified) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-green-600" />
            <h3 className="text-xl font-semibold">Due Diligence Complete</h3>
          </div>
          <Badge className="bg-green-100 text-green-700 px-3 py-1">
            Verified {new Date(data.verifiedAt || '').toLocaleDateString()}
          </Badge>
        </div>

        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Startup Readiness Score</p>
                <p className="text-3xl font-bold text-green-600">{data.readinessScore}/100</p>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">Risk Level</p>
                <Badge className={
                  data.riskLevel === 'low' ? 'bg-green-500' :
                  data.riskLevel === 'medium' ? 'bg-yellow-500' :
                  'bg-red-500'
                }>
                  {data.riskLevel?.toUpperCase()}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {data.recommendedActions && data.recommendedActions.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recommended Actions</CardTitle>
              <CardDescription>Complete these before launch</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1">
                {data.recommendedActions.map((action, idx) => (
                  <li key={idx} className="text-sm">{action}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="summary">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="details">Full Details</TabsTrigger>
            <TabsTrigger value="actions">Next Steps</TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Founders</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg font-semibold">{data.founders?.length}</p>
                  <p className="text-xs text-muted-foreground">team members</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Anchor</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium">{data.anchorCompany}</p>
                  <p className="text-xs text-muted-foreground">{data.commitmentLevel}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Registration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium">{data.registrationStatus}</p>
                  {data.businessName && (
                    <p className="text-xs text-muted-foreground">{data.businessName}</p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Bank Account</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm font-medium">{data.bankStatus}</p>
                  {data.bankName && (
                    <p className="text-xs text-muted-foreground">{data.bankName}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {data.verificationNotes && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{data.verificationNotes}</AlertDescription>
              </Alert>
            )}

            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-blue-800">Ready for Funding Stage</h4>
                    <p className="text-sm text-blue-600">
                      Due diligence is complete. Proceed to funding and milestone tracking.
                    </p>
                  </div>
                  <Button 
                    onClick={() => updatePitchStage(pitch.id, 'funding')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Go to Funding
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="details" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Founders</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {data.founders?.map((founder, idx) => (
                  <div key={idx} className="border-b last:border-0 pb-4 last:pb-0">
                    <p className="font-medium">{founder.name}</p>
                    <p className="text-sm text-muted-foreground">{founder.role}</p>
                    <p className="text-xs">{founder.email}</p>
                    {founder.timeCommitment && (
                      <Badge variant="outline" className="mt-2">
                        {founder.timeCommitment}
                      </Badge>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Anchor Commitment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Row label="Company" value={data.anchorCompany} />
                <Row label="Contact" value={data.anchorContact} />
                <Row label="Commitment Level" value={data.commitmentLevel} />
                {data.retainerPaid && (
                  <Row label="Retainer" value={`KES ${data.retainerAmount?.toLocaleString()}`} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Legal Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Row label="Registration" value={data.registrationStatus} />
                {data.businessName && <Row label="Business Name" value={data.businessName} />}
                {data.registrationNumber && <Row label="Reg Number" value={data.registrationNumber} />}
                <Row label="Bank Account" value={data.bankStatus} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="actions" className="space-y-4 pt-4">
            <Card>
              <CardHeader>
                <CardTitle>Next Steps</CardTitle>
              </CardHeader>
              <CardContent>
                {data.milestones && data.milestones.length > 0 ? (
                  <div className="space-y-4">
                    {data.milestones.map((milestone, idx) => (
                      <div key={idx} className="flex items-center justify-between border-b pb-2">
                        <div>
                          <p className="font-medium">{milestone.name}</p>
                          <p className="text-xs text-muted-foreground">Due: {new Date(milestone.targetDate).toLocaleDateString()}</p>
                        </div>
                        <Badge variant={milestone.status === 'completed' ? 'default' : 'outline'}>
                          {milestone.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No milestones set</p>
                )}
                
                {data.expectedLaunchDate && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm font-medium">Expected Launch</p>
                    <p className="text-sm">{new Date(data.expectedLaunchDate).toLocaleDateString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // If submitted but not verified
  if (data && !data.verified) {
    return (
      <div className="space-y-6">
        <Alert className="bg-yellow-50 border-yellow-200">
          <Hourglass className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-700">
            Due diligence submitted. Awaiting analyst review.
          </AlertDescription>
        </Alert>

        {isAssignedAnalyst && (
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle>Analyst Review</CardTitle>
              <CardDescription>
                Review the startup information and verify.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Verification Notes</Label>
                <Textarea
                  placeholder="Add your assessment notes..."
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label>Risk Assessment</Label>
                <Select value={riskLevel} onValueChange={(v: any) => setRiskLevel(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">✅ Low Risk - Ready to proceed</SelectItem>
                    <SelectItem value="medium">🟡 Medium Risk - Monitor closely</SelectItem>
                    <SelectItem value="high">🔴 High Risk - Needs discussion</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Recommended Actions (for founder)</Label>
                <Textarea
                  placeholder="List any actions the founder should complete..."
                  value={recommendedActions.join('\n')}
                  onChange={(e) => setRecommendedActions(e.target.value.split('\n').filter(Boolean))}
                  rows={3}
                />
              </div>

              <Button onClick={handleVerify} className="w-full bg-green-600 hover:bg-green-700">
                <Check className="h-4 w-4 mr-2" />
                Verify & Move to Funding
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Form for founders
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold">Startup Due Diligence</h3>
        <Badge variant="outline">Pre-Launch Startup</Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 md:grid-cols-6">
          <TabsTrigger value="founders">Founders</TabsTrigger>
          <TabsTrigger value="problem">Problem</TabsTrigger>
          <TabsTrigger value="anchor">Anchor</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
          <TabsTrigger value="legal">Legal</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        {/* FOUNDERS TAB */}
        <TabsContent value="founders" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Founder Information
              </CardTitle>
              <CardDescription>
                Tell us about the people starting this venture.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {form.founders?.map((founder, index) => (
                <div key={founder.id} className="space-y-4 p-4 border rounded-lg relative">
                  <div className="absolute top-4 right-4">
                    {index > 0 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeFounder(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  
                  <h4 className="font-medium">Founder {index + 1}</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name *</Label>
                      <Input
                        value={founder.name}
                        onChange={(e) => updateFounder(index, 'name', e.target.value)}
                        placeholder="e.g., Jane Doe"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Role *</Label>
                      <Input
                        value={founder.role}
                        onChange={(e) => updateFounder(index, 'role', e.target.value)}
                        placeholder="e.g., CEO"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={founder.email}
                        onChange={(e) => updateFounder(index, 'email', e.target.value)}
                        placeholder="jane@company.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input
                        value={founder.phone || ''}
                        onChange={(e) => updateFounder(index, 'phone', e.target.value)}
                        placeholder="+254 712 345 678"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>ID/Passport Number</Label>
                      <Input
                        value={founder.idNumber || ''}
                        onChange={(e) => updateFounder(index, 'idNumber', e.target.value)}
                        placeholder="e.g., 12345678"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>LinkedIn Profile</Label>
                      <Input
                        value={founder.linkedin || ''}
                        onChange={(e) => updateFounder(index, 'linkedin', e.target.value)}
                        placeholder="linkedin.com/in/janedoe"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Relevant Experience</Label>
                    <Textarea
                      value={founder.experience}
                      onChange={(e) => updateFounder(index, 'experience', e.target.value)}
                      placeholder="Briefly describe your background relevant to this venture"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Time Commitment</Label>
                      <Select 
                        value={founder.timeCommitment}
                        onValueChange={(v: any) => updateFounder(index, 'timeCommitment', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full-time">Full-Time</SelectItem>
                          <SelectItem value="part-time">Part-Time</SelectItem>
                          <SelectItem value="advisory">Advisory</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Equity (%)</Label>
                      <Input
                        type="number"
                        value={founder.equity}
                        onChange={(e) => updateFounder(index, 'equity', parseInt(e.target.value))}
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>ID Document Upload</Label>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const url = URL.createObjectURL(file);
                          updateFounder(index, 'idDocument', url);
                        }
                      }}
                      className="cursor-pointer"
                    />
                    {founder.idDocument && (
                      <div className="flex items-center gap-2 mt-2">
                        <FileText className="h-4 w-4 text-green-600" />
                        <span className="text-xs text-green-600">Document uploaded</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(founder.idDocument, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <Button type="button" variant="outline" onClick={addFounder}>
                <Plus className="h-4 w-4 mr-2" />
                Add Another Founder
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PROBLEM VALIDATION TAB */}
        <TabsContent value="problem" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Problem Validation
              </CardTitle>
              <CardDescription>
                This information comes from your Problem Bank submission.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Problem ID</Label>
                <Input value={form.problemId} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <Label>Problem Statement</Label>
                <Textarea 
                  value={form.problemStatement} 
                  disabled 
                  className="bg-muted"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Solution Hypothesis</Label>
                <Textarea 
                  value={form.solutionHypothesis} 
                  disabled 
                  className="bg-muted"
                  rows={3}
                />
              </div>

              <div className="flex items-center gap-2">
                <CheckCircle className={`h-5 w-5 ${form.problemVerified ? 'text-green-600' : 'text-yellow-600'}`} />
                <span className="text-sm">
                  {form.problemVerified ? 'Verified in Problem Bank' : 'Pending verification'}
                </span>
              </div>

              <div className="space-y-2">
                <Label>Evidence Links (from Problem Bank)</Label>
                <Textarea
                  placeholder="Links to photos, videos, data files"
                  value={form.evidenceLinks?.join('\n') || ''}
                  onChange={(e) => updateField('evidenceLinks', e.target.value.split('\n').filter(Boolean))}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">
                  Add links to evidence from your Problem Bank submission
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ANCHOR COMMITMENT TAB */}
        <TabsContent value="anchor" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Anchor Customer
              </CardTitle>
              <CardDescription>
                Tell us about the customer committed to this solution.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={form.anchorCompany}
                    onChange={(e) => updateField('anchorCompany', e.target.value)}
                    placeholder="e.g., Kenya Breweries Ltd"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Person *</Label>
                  <Input
                    value={form.anchorContact}
                    onChange={(e) => updateField('anchorContact', e.target.value)}
                    placeholder="e.g., John Manager"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={form.anchorEmail}
                    onChange={(e) => updateField('anchorEmail', e.target.value)}
                    placeholder="john@company.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={form.anchorPhone || ''}
                    onChange={(e) => updateField('anchorPhone', e.target.value)}
                    placeholder="+254 712 345 678"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Commitment Level</Label>
                <Select 
                  value={form.commitmentLevel}
                  onValueChange={(v: any) => updateField('commitmentLevel', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="verbal">Verbal Interest</SelectItem>
                    <SelectItem value="loi_drafted">LOI Drafted</SelectItem>
                    <SelectItem value="loi_signed">LOI Signed</SelectItem>
                    <SelectItem value="retainer_paid">Retainer Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.commitmentLevel === 'loi_signed' && (
                <div className="space-y-2">
                  <Label>Upload LOI Document</Label>
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const url = URL.createObjectURL(file);
                        updateField('loiDocument', url);
                      }
                    }}
                    className="cursor-pointer"
                  />
                </div>
              )}

              {form.commitmentLevel === 'retainer_paid' && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-2">
                    <Label>Retainer Amount (KES)</Label>
                    <Input
                      type="number"
                      value={form.retainerAmount || ''}
                      onChange={(e) => updateField('retainerAmount', parseInt(e.target.value))}
                      placeholder="50000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Date</Label>
                    <Input
                      type="date"
                      value={form.retainerDate || ''}
                      onChange={(e) => updateField('retainerDate', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEAM CAPABILITY TAB */}
        <TabsContent value="team" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Team Capability
              </CardTitle>
              <CardDescription>
                Tell us about your team's readiness to execute.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Current Team Size</Label>
                  <Input
                    type="number"
                    value={form.teamSize}
                    onChange={(e) => updateField('teamSize', parseInt(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Office/Workspace</Label>
                  <Select 
                    value={form.officeStatus}
                    onValueChange={(v: any) => updateField('officeStatus', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None Yet</SelectItem>
                      <SelectItem value="home">Home Office</SelectItem>
                      <SelectItem value="coworking">Coworking Space</SelectItem>
                      <SelectItem value="leased">Leased Office</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.officeStatus !== 'none' && (
                <div className="space-y-2">
                  <Label>Office Address</Label>
                  <Input
                    value={form.officeAddress || ''}
                    onChange={(e) => updateField('officeAddress', e.target.value)}
                    placeholder="e.g., 123 Kenyatta Avenue, Nairobi"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Key Roles Covered (one per line)</Label>
                <Textarea
                  value={form.keyRoles?.join('\n') || ''}
                  onChange={(e) => updateField('keyRoles', e.target.value.split('\n').filter(Boolean))}
                  placeholder="CEO&#10;CTO&#10;COO"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Hiring Plan (who else do you need?)</Label>
                <Textarea
                  value={form.hiringPlan || ''}
                  onChange={(e) => updateField('hiringPlan', e.target.value)}
                  placeholder="e.g., Need to hire a developer and a sales person"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* LEGAL READINESS TAB */}
        <TabsContent value="legal" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                Business Registration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Registration Status</Label>
                <Select 
                  value={form.registrationStatus}
                  onValueChange={(v: any) => updateField('registrationStatus', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="not_applicable">Not Applicable</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.registrationStatus !== 'not_started' && form.registrationStatus !== 'not_applicable' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Business Name</Label>
                      <Input
                        value={form.businessName || ''}
                        onChange={(e) => updateField('businessName', e.target.value)}
                        placeholder="e.g., EcoTech Solutions Ltd"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Business Type</Label>
                      <Select 
                        value={form.businessType}
                        onValueChange={(v: any) => updateField('businessType', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="llc">LLC</SelectItem>
                          <SelectItem value="company">Company</SelectItem>
                          <SelectItem value="cooperative">Cooperative</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.registrationStatus === 'completed' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Registration Number</Label>
                          <Input
                            value={form.registrationNumber || ''}
                            onChange={(e) => updateField('registrationNumber', e.target.value)}
                            placeholder="e.g., CPR/2024/12345"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Registration Date</Label>
                          <Input
                            type="date"
                            value={form.registrationDate || ''}
                            onChange={(e) => updateField('registrationDate', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Certificate of Incorporation</Label>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const url = URL.createObjectURL(file);
                              updateField('certificateOfIncorporation', url);
                            }
                          }}
                          className="cursor-pointer"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>PIN Certificate</Label>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const url = URL.createObjectURL(file);
                              updateField('pinCertificate', url);
                            }
                          }}
                          className="cursor-pointer"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Business Permit</Label>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const url = URL.createObjectURL(file);
                              updateField('businessPermit', url);
                            }
                          }}
                          className="cursor-pointer"
                        />
                      </div>
                    </>
                  )}

                  {form.registrationStatus === 'in_progress' && (
                    <div className="space-y-2">
                      <Label>What's pending?</Label>
                      <Textarea
                        value={form.registrationNotes || ''}
                        onChange={(e) => updateField('registrationNotes', e.target.value)}
                        placeholder="e.g., Awaiting PIN from KRA"
                        rows={3}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Landmark className="h-5 w-5" />
                Bank Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Bank Account Status</Label>
                <Select 
                  value={form.bankStatus}
                  onValueChange={(v: any) => updateField('bankStatus', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="not_applicable">Not Applicable</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.bankStatus === 'completed' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        value={form.bankName || ''}
                        onChange={(e) => updateField('bankName', e.target.value)}
                        placeholder="e.g., Equity Bank"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Account Number</Label>
                      <Input
                        value={form.bankAccount || ''}
                        onChange={(e) => updateField('bankAccount', e.target.value)}
                        placeholder="e.g., 1234567890"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Bank Confirmation Letter</Label>
                    <Input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const url = URL.createObjectURL(file);
                          updateField('bankConfirmation', url);
                        }
                      }}
                      className="cursor-pointer"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Licenses & Permits
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.licenses?.map((license, index) => (
                <div key={license.id} className="space-y-4 p-4 border rounded-lg">
                  <div className="flex justify-between items-center">
                    <h4 className="font-medium">License {index + 1}</h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLicense(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>License Name</Label>
                      <Input
                        value={license.name}
                        onChange={(e) => updateLicense(index, 'name', e.target.value)}
                        placeholder="e.g., Food Hygiene Certificate"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={license.status}
                        onValueChange={(v: any) => updateLicense(index, 'status', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="not_applicable">Not Applicable</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {license.status === 'completed' && (
                    <>
                      <div className="space-y-2">
                        <Label>Document</Label>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const url = URL.createObjectURL(file);
                              updateLicense(index, 'document', url);
                            }
                          }}
                          className="cursor-pointer"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Expiry Date</Label>
                          <Input
                            type="date"
                            value={license.expiryDate || ''}
                            onChange={(e) => updateLicense(index, 'expiryDate', e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {license.status === 'in_progress' && (
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Input
                        value={license.notes || ''}
                        onChange={(e) => updateLicense(index, 'notes', e.target.value)}
                        placeholder="e.g., Application submitted, awaiting processing"
                      />
                    </div>
                  )}
                </div>
              ))}

              <Button type="button" variant="outline" onClick={addLicense}>
                <Plus className="h-4 w-4 mr-2" />
                Add License/Permit
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TIMELINE TAB */}
        <TabsContent value="timeline" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Launch Timeline
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Expected Launch Date</Label>
                <Input
                  type="date"
                  value={form.expectedLaunchDate || ''}
                  onChange={(e) => updateField('expectedLaunchDate', e.target.value)}
                />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <Label>Key Milestones</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addMilestone}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Milestone
                  </Button>
                </div>

                {form.milestones?.map((milestone, index) => (
                  <div key={milestone.id} className="grid grid-cols-3 gap-2 items-end">
                    <div className="space-y-2">
                      <Label>Milestone</Label>
                      <Input
                        value={milestone.name}
                        onChange={(e) => updateMilestone(index, 'name', e.target.value)}
                        placeholder="e.g., Complete registration"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Target Date</Label>
                      <Input
                        type="date"
                        value={milestone.targetDate}
                        onChange={(e) => updateMilestone(index, 'targetDate', e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        value={milestone.status}
                        onValueChange={(v: any) => updateMilestone(index, 'status', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMilestone(index)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button type="submit" size="lg">
          Submit Due Diligence
        </Button>
      </div>
    </form>
  );
}

// Helper Components
function Row({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex justify-between border-b pb-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}